/**
 * Small games to play while Media AI makes a picture or video (like
 * ChatGPT's loader game): Snake, Tank and a Mario-style runner. Each plays
 * with the keyboard OR the mouse / a finger:
 *   Snake — arrows / WASD, or click / tap on the side to turn towards (swipes too)
 *   Tank  — move with arrows / WASD, aim with the mouse, Space or click to fire;
 *           hold the mouse button to drive towards the pointer
 *   Mario — Space / ↑ / W or click / tap to jump (hold for higher), ← → to slow / speed
 * Keys only reach a game while it has focus (click it first), so typing in the
 * message box is never taken over. Best scores are remembered on this device.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../utils/i18n';

const W = 480;
const H = 270;
const BEST_KEY = 'mediaAI.games.best';
const GAME_KEY = 'mediaAI.games.last';

const readBest = () => {
  try { return JSON.parse(localStorage.getItem(BEST_KEY)) || {}; } catch { return {}; }
};
const writeBest = (best) => {
  try { localStorage.setItem(BEST_KEY, JSON.stringify(best)); } catch { /* private mode — not remembered */ }
};
const readGame = () => {
  try { return localStorage.getItem(GAME_KEY); } catch { return null; }
};
const writeGame = (id) => {
  try { localStorage.setItem(GAME_KEY, id); } catch { /* not remembered */ }
};

const DIRS = {
  ArrowUp: [0, -1], w: [0, -1], W: [0, -1],
  ArrowDown: [0, 1], s: [0, 1], S: [0, 1],
  ArrowLeft: [-1, 0], a: [-1, 0], A: [-1, 0],
  ArrowRight: [1, 0], d: [1, 0], D: [1, 0],
};

const rand = (min, max) => min + Math.random() * (max - min);

function text(ctx, str, x, y, { size = 14, color = '#fff', align = 'center', weight = 600 } = {}) {
  ctx.font = `${weight} ${size}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(str, x, y);
}

function overlay(ctx, title, hint) {
  ctx.fillStyle = 'rgba(15, 23, 42, 0.72)';
  ctx.fillRect(0, 0, W, H);
  text(ctx, title, W / 2, H / 2 - 14, { size: 22, weight: 800 });
  text(ctx, hint, W / 2, H / 2 + 16, { size: 13, color: '#cbd5e1', weight: 500 });
}

// ─── Snake ───────────────────────────────────────────────────────────────────

function createSnake() {
  const CELL = 18;
  const COLS = Math.floor(W / CELL);
  const ROWS = Math.floor(H / CELL);
  const g = {};
  const placeFood = () => {
    do { g.food = [Math.floor(rand(0, COLS)), Math.floor(rand(0, ROWS))]; }
    while (g.body.some(([x, y]) => x === g.food[0] && y === g.food[1]));
  };
  const reset = () => {
    g.body = [[6, 7], [5, 7], [4, 7]];
    g.dir = [1, 0];
    g.queue = [];
    g.step = 0.13;
    g.acc = 0;
    g.score = 0;
    g.state = 'ready';
    placeFood();
  };
  const turn = ([dx, dy]) => {
    const last = g.queue.length ? g.queue[g.queue.length - 1] : g.dir;
    if (dx === -last[0] && dy === -last[1]) return; // no reversing into itself
    if (dx === last[0] && dy === last[1]) return;
    if (g.queue.length < 3) g.queue.push([dx, dy]);
  };
  const start = () => { if (g.state !== 'playing') { if (g.state === 'over') reset(); g.state = 'playing'; } };
  reset();
  return {
    get score() { return g.score; },
    get over() { return g.state === 'over'; },
    reset,
    key(e) {
      if (DIRS[e.key]) { start(); turn(DIRS[e.key]); return true; }
      if (e.key === ' ' || e.key === 'Enter') { start(); return true; }
      return false;
    },
    pointer(type, x, y) {
      if (type !== 'down') return;
      if (g.state !== 'playing') { start(); return; }
      // Turn towards the click, across the current direction.
      const [hx, hy] = g.body[0];
      const dx = x - (hx + 0.5) * CELL;
      const dy = y - (hy + 0.5) * CELL;
      const last = g.queue.length ? g.queue[g.queue.length - 1] : g.dir;
      if (last[0] !== 0) turn([0, dy < 0 ? -1 : 1]);
      else turn([dx < 0 ? -1 : 1, 0]);
    },
    swipe(dx, dy) {
      start();
      turn(Math.abs(dx) > Math.abs(dy) ? [Math.sign(dx), 0] : [0, Math.sign(dy)]);
    },
    update(dt) {
      if (g.state !== 'playing') return;
      g.acc += dt;
      while (g.acc >= g.step) {
        g.acc -= g.step;
        if (g.queue.length) g.dir = g.queue.shift();
        const head = [g.body[0][0] + g.dir[0], g.body[0][1] + g.dir[1]];
        const hitWall = head[0] < 0 || head[1] < 0 || head[0] >= COLS || head[1] >= ROWS;
        if (hitWall || g.body.some(([x, y]) => x === head[0] && y === head[1])) { g.state = 'over'; return; }
        g.body.unshift(head);
        if (head[0] === g.food[0] && head[1] === g.food[1]) {
          g.score += 1;
          g.step = Math.max(0.06, g.step * 0.97);
          placeFood();
        } else {
          g.body.pop();
        }
      }
    },
    draw(ctx, t) {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#16213a';
      for (let x = 0; x < COLS; x++) for (let y = 0; y < ROWS; y++) if ((x + y) % 2) ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
      // Food: a pulsing yellow dot.
      const pulse = 2 + Math.sin(t * 6) * 1.5;
      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.arc((g.food[0] + 0.5) * CELL, (g.food[1] + 0.5) * CELL, CELL / 2 - 3 + pulse / 2, 0, Math.PI * 2);
      ctx.fill();
      g.body.forEach(([x, y], i) => {
        ctx.fillStyle = i === 0 ? '#60a5fa' : `hsl(217, 85%, ${Math.max(38, 62 - i)}%)`;
        ctx.beginPath();
        ctx.roundRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2, 5);
        ctx.fill();
      });
      const [hx, hy] = g.body[0];
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc((hx + 0.5) * CELL + g.dir[0] * 3 - g.dir[1] * 4, (hy + 0.5) * CELL + g.dir[1] * 3 - g.dir[0] * 4, 2, 0, Math.PI * 2);
      ctx.arc((hx + 0.5) * CELL + g.dir[0] * 3 + g.dir[1] * 4, (hy + 0.5) * CELL + g.dir[1] * 3 + g.dir[0] * 4, 2, 0, Math.PI * 2);
      ctx.fill();
      text(ctx, `${g.score}`, W - 12, 14, { align: 'right', size: 13, color: '#94a3b8' });
      if (g.state === 'ready') overlay(ctx, 'Snake', 'Arrow keys / WASD, or click where to turn');
      if (g.state === 'over') overlay(ctx, `Game over · ${g.score}`, 'Click or press Space to play again');
    },
  };
}

// ─── Tank ────────────────────────────────────────────────────────────────────

function createTank() {
  const g = {};
  const reset = () => {
    g.p = { x: W / 2, y: H - 50, angle: -Math.PI / 2, aim: -Math.PI / 2 };
    g.keys = new Set();
    g.mouse = null; // { x, y, down } while the pointer is over the game
    g.mouseAt = 0;
    g.bullets = [];
    g.enemies = [];
    g.bursts = [];
    g.lives = 3;
    g.score = 0;
    g.cool = 0;
    g.spawn = 1.2;
    g.hurt = 0;
    g.time = 0;
    g.state = 'ready';
  };
  const start = () => { if (g.state !== 'playing') { if (g.state === 'over') reset(); g.state = 'playing'; } };
  const fire = (from, angle, enemy) => {
    g.bullets.push({ x: from.x + Math.cos(angle) * 20, y: from.y + Math.sin(angle) * 20, vx: Math.cos(angle) * (enemy ? 170 : 340), vy: Math.sin(angle) * (enemy ? 170 : 340), enemy, life: 2 });
  };
  const playerFire = () => {
    if (g.cool > 0) return;
    fire(g.p, g.p.aim, false);
    g.cool = 0.28;
  };
  const drawTank = (ctx, { x, y, angle, aim }, body, turret) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(-15, -13, 30, 5);
    ctx.fillRect(-15, 8, 30, 5);
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.roundRect(-13, -9, 26, 18, 4);
    ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(aim);
    ctx.fillStyle = turret;
    ctx.fillRect(0, -2.5, 20, 5);
    ctx.beginPath();
    ctx.arc(0, 0, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };
  reset();
  return {
    get score() { return g.score; },
    get over() { return g.state === 'over'; },
    reset,
    key(e, down) {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (DIRS[e.key] || ['w', 'a', 's', 'd'].includes(k)) {
        if (down) { start(); g.keys.add(k); } else g.keys.delete(k);
        return true;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        if (down) { if (g.state !== 'playing') start(); else playerFire(); }
        return true;
      }
      return false;
    },
    pointer(type, x, y) {
      if (type === 'leave') { g.mouse = null; return; }
      g.mouse = { x, y, down: type === 'down' ? true : type === 'up' ? false : g.mouse?.down || false };
      g.mouseAt = g.time;
      if (type === 'down') { if (g.state !== 'playing') start(); else playerFire(); }
    },
    update(dt) {
      if (g.state !== 'playing') return;
      g.time += dt;
      g.cool = Math.max(0, g.cool - dt);
      g.hurt = Math.max(0, g.hurt - dt);
      const p = g.p;
      // Drive: keys, or hold the mouse button to drive towards the pointer.
      let mx = 0;
      let my = 0;
      if (g.keys.has('ArrowLeft') || g.keys.has('a')) mx -= 1;
      if (g.keys.has('ArrowRight') || g.keys.has('d')) mx += 1;
      if (g.keys.has('ArrowUp') || g.keys.has('w')) my -= 1;
      if (g.keys.has('ArrowDown') || g.keys.has('s')) my += 1;
      if (!mx && !my && g.mouse?.down) {
        const dx = g.mouse.x - p.x;
        const dy = g.mouse.y - p.y;
        const d = Math.hypot(dx, dy);
        if (d > 40) { mx = dx / d; my = dy / d; }
        playerFire(); // holding the button keeps firing
      }
      if (mx || my) {
        const len = Math.hypot(mx, my);
        p.x = Math.max(16, Math.min(W - 16, p.x + (mx / len) * 120 * dt));
        p.y = Math.max(16, Math.min(H - 16, p.y + (my / len) * 120 * dt));
        p.angle = Math.atan2(my, mx);
      }
      // Aim: at the mouse if it moved lately, else where the tank is heading.
      p.aim = g.mouse && g.time - g.mouseAt < 3 ? Math.atan2(g.mouse.y - p.y, g.mouse.x - p.x) : p.angle;

      g.spawn -= dt;
      if (g.spawn <= 0 && g.enemies.length < 3 + Math.floor(g.score / 50)) {
        const side = Math.floor(rand(0, 3));
        const e = side === 0 ? { x: rand(20, W - 20), y: -16 } : side === 1 ? { x: -16, y: rand(20, H / 2) } : { x: W + 16, y: rand(20, H / 2) };
        g.enemies.push({ ...e, angle: 0, aim: 0, speed: rand(28, 45) + g.score / 8, reload: rand(1.2, 2.6) });
        g.spawn = Math.max(0.6, 2 - g.score / 200);
      }
      for (const e of g.enemies) {
        const a = Math.atan2(p.y - e.y, p.x - e.x);
        e.aim = a;
        e.angle = a;
        if (Math.hypot(p.x - e.x, p.y - e.y) > 90) { e.x += Math.cos(a) * e.speed * dt; e.y += Math.sin(a) * e.speed * dt; }
        e.reload -= dt;
        if (e.reload <= 0 && e.y > 0 && e.x > 0 && e.x < W) { fire(e, a + rand(-0.12, 0.12), true); e.reload = rand(1.6, 3); }
      }
      for (const b of g.bullets) { b.x += b.vx * dt; b.y += b.vy * dt; b.life -= dt; }
      for (const b of g.bullets) {
        if (b.life <= 0) continue;
        if (b.enemy) {
          if (!g.hurt && Math.hypot(b.x - p.x, b.y - p.y) < 14) {
            b.life = 0;
            g.lives -= 1;
            g.hurt = 1.2;
            g.bursts.push({ x: p.x, y: p.y, t: 0 });
            if (g.lives <= 0) g.state = 'over';
          }
        } else {
          const hit = g.enemies.find((e) => Math.hypot(b.x - e.x, b.y - e.y) < 15);
          if (hit) {
            b.life = 0;
            hit.dead = true;
            g.score += 10;
            g.bursts.push({ x: hit.x, y: hit.y, t: 0 });
          }
        }
      }
      g.enemies = g.enemies.filter((e) => !e.dead);
      g.bullets = g.bullets.filter((b) => b.life > 0 && b.x > -20 && b.x < W + 20 && b.y > -20 && b.y < H + 20);
      g.bursts.forEach((b) => { b.t += dt; });
      g.bursts = g.bursts.filter((b) => b.t < 0.4);
    },
    draw(ctx) {
      ctx.fillStyle = '#3f4e2b';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(0,0,0,0.08)';
      for (let x = 0; x < W; x += 30) for (let y = (x / 30) % 2 ? 0 : 15; y < H; y += 30) ctx.fillRect(x, y, 15, 15);
      for (const e of g.enemies) drawTank(ctx, e, '#b91c1c', '#7f1d1d');
      if (!(g.hurt && Math.floor(g.hurt * 10) % 2)) drawTank(ctx, g.p, '#2563eb', '#1e3a8a');
      for (const b of g.bullets) {
        ctx.fillStyle = b.enemy ? '#fca5a5' : '#fde047';
        ctx.beginPath();
        ctx.arc(b.x, b.y, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      for (const b of g.bursts) {
        ctx.fillStyle = `rgba(251, 146, 60, ${1 - b.t / 0.4})`;
        ctx.beginPath();
        ctx.arc(b.x, b.y, 8 + b.t * 50, 0, Math.PI * 2);
        ctx.fill();
      }
      text(ctx, `${g.score}`, W - 12, 14, { align: 'right', size: 13, color: '#e2e8f0' });
      text(ctx, '♥'.repeat(Math.max(0, g.lives)), 12, 14, { align: 'left', size: 14, color: '#f87171' });
      if (g.state === 'ready') overlay(ctx, 'Tank', 'Move: arrows / WASD · aim with the mouse · fire: Space or click');
      if (g.state === 'over') overlay(ctx, `Game over · ${g.score}`, 'Click or press Space to play again');
    },
  };
}

// ─── Mario-style runner ──────────────────────────────────────────────────────

function createRunner() {
  const GROUND = H - 40;
  const g = {};
  const reset = () => {
    g.p = { x: 80, y: GROUND, vy: 0, onGround: true };
    g.speed = 190;
    g.boost = 0;
    g.obstacles = [];
    g.coins = [];
    g.clouds = Array.from({ length: 4 }, (_, i) => ({ x: i * 140 + rand(0, 60), y: rand(25, 90) }));
    g.next = 1;
    g.coinNext = 0.6;
    g.dist = 0;
    g.coinsTaken = 0;
    g.holding = false;
    g.time = 0;
    g.state = 'ready';
  };
  const start = () => { if (g.state !== 'playing') { if (g.state === 'over') reset(); g.state = 'playing'; } };
  const jump = () => {
    if (g.state !== 'playing') { start(); return; }
    if (g.p.onGround) { g.p.vy = -430; g.p.onGround = false; }
    g.holding = true;
  };
  const score = () => Math.floor(g.dist / 10) + g.coinsTaken * 10;
  reset();
  return {
    get score() { return score(); },
    get over() { return g.state === 'over'; },
    reset,
    key(e, down) {
      if ([' ', 'ArrowUp', 'w', 'W', 'Enter'].includes(e.key)) {
        if (down) { if (!e.repeat) jump(); } else g.holding = false;
        return true;
      }
      if (['ArrowLeft', 'a', 'A'].includes(e.key)) { g.boost = down ? -60 : 0; return true; }
      if (['ArrowRight', 'd', 'D'].includes(e.key)) { g.boost = down ? 80 : 0; return true; }
      return false;
    },
    pointer(type) {
      if (type === 'down') jump();
      if (type === 'up' || type === 'leave') g.holding = false;
    },
    update(dt) {
      if (g.state !== 'playing') return;
      g.time += dt;
      const speed = g.speed + g.boost;
      g.speed = Math.min(380, g.speed + dt * 4);
      g.dist += speed * dt;
      const p = g.p;
      // Holding the jump button while rising jumps higher.
      p.vy += (g.holding && p.vy < 0 ? 900 : 1700) * dt;
      p.y += p.vy * dt;
      if (p.y >= GROUND) { p.y = GROUND; p.vy = 0; p.onGround = true; }

      g.next -= dt;
      if (g.next <= 0) {
        const kind = Math.random() < 0.55 ? 'pipe' : 'walker';
        g.obstacles.push(kind === 'pipe'
          ? { kind, x: W + 20, w: 34, h: rand(30, 58) }
          : { kind, x: W + 20, w: 26, h: 22, phase: rand(0, 6) });
        g.next = rand(0.9, 1.7) * (220 / speed);
      }
      g.coinNext -= dt;
      if (g.coinNext <= 0) {
        const y = GROUND - rand(60, 120);
        for (let i = 0; i < 3; i++) g.coins.push({ x: W + 20 + i * 24, y });
        g.coinNext = rand(1.4, 2.6);
      }
      for (const o of g.obstacles) o.x -= (speed + (o.kind === 'walker' ? 40 : 0)) * dt;
      for (const c of g.coins) c.x -= speed * dt;
      for (const cl of g.clouds) { cl.x -= speed * 0.2 * dt; if (cl.x < -60) { cl.x = W + rand(0, 80); cl.y = rand(25, 90); } }
      g.obstacles = g.obstacles.filter((o) => o.x + o.w > -10);

      // Hitbox a little smaller than the drawing, so near misses feel fair.
      const px1 = p.x - 9;
      const px2 = p.x + 9;
      const py1 = p.y - 30;
      for (const o of g.obstacles) {
        if (px2 > o.x + 3 && px1 < o.x + o.w - 3 && p.y > GROUND - o.h + 3) {
          // Landing on a walker squashes it (as in the classic game).
          if (o.kind === 'walker' && p.vy > 0 && p.y - 10 < GROUND - o.h + 6) {
            o.x = -100;
            p.vy = -330;
            g.coinsTaken += 2;
          } else {
            g.state = 'over';
          }
        }
      }
      g.coins = g.coins.filter((c) => {
        if (c.x < -10) return false;
        if (Math.abs(c.x - p.x) < 16 && c.y > py1 - 8 && c.y < p.y + 4) { g.coinsTaken += 1; return false; }
        return true;
      });
    },
    draw(ctx) {
      const sky = ctx.createLinearGradient(0, 0, 0, H);
      sky.addColorStop(0, '#60a5fa');
      sky.addColorStop(1, '#bfdbfe');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      for (const cl of g.clouds) {
        ctx.beginPath();
        ctx.arc(cl.x, cl.y, 14, 0, Math.PI * 2);
        ctx.arc(cl.x + 16, cl.y - 6, 17, 0, Math.PI * 2);
        ctx.arc(cl.x + 34, cl.y, 13, 0, Math.PI * 2);
        ctx.fill();
      }
      // Ground: brick strip.
      ctx.fillStyle = '#b45309';
      ctx.fillRect(0, GROUND, W, H - GROUND);
      ctx.fillStyle = '#92400e';
      const off = Math.floor(g.dist) % 24;
      for (let x = -off; x < W; x += 24) {
        ctx.fillRect(x, GROUND, 2, H - GROUND);
        ctx.fillRect(x + 12, GROUND + 20, 2, 20);
      }
      ctx.fillRect(0, GROUND + 19, W, 2);
      ctx.fillStyle = '#16a34a';
      ctx.fillRect(0, GROUND - 4, W, 5);

      for (const c of g.coins) {
        ctx.fillStyle = '#facc15';
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, 5 + Math.abs(Math.sin(g.time * 5 + c.x / 30)) * 2, 8, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      for (const o of g.obstacles) {
        if (o.kind === 'pipe') {
          ctx.fillStyle = '#16a34a';
          ctx.fillRect(o.x + 3, GROUND - o.h + 10, o.w - 6, o.h - 10);
          ctx.fillStyle = '#22c55e';
          ctx.fillRect(o.x, GROUND - o.h, o.w, 12);
          ctx.fillStyle = 'rgba(255,255,255,0.25)';
          ctx.fillRect(o.x + 6, GROUND - o.h + 12, 4, o.h - 14);
        } else {
          const bob = Math.sin(g.time * 12 + o.phase) * 1.5;
          ctx.fillStyle = '#7c2d12';
          ctx.beginPath();
          ctx.ellipse(o.x + o.w / 2, GROUND - o.h / 2 + bob, o.w / 2, o.h / 2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.fillRect(o.x + 6, GROUND - o.h + 6 + bob, 4, 5);
          ctx.fillRect(o.x + 16, GROUND - o.h + 6 + bob, 4, 5);
        }
      }
      // The runner: red cap and shirt, blue overalls.
      const { x, y } = g.p;
      const legs = g.p.onGround ? Math.sin(g.time * 22) * 4 : 3;
      ctx.fillStyle = '#1d4ed8';
      ctx.fillRect(x - 7, y - 8, 5, 8 + legs);
      ctx.fillRect(x + 2, y - 8, 5, 8 - legs);
      ctx.fillRect(x - 8, y - 18, 16, 11);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(x - 9, y - 22, 18, 6);
      ctx.fillStyle = '#fcd9b6';
      ctx.fillRect(x - 6, y - 32, 13, 11);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(x - 7, y - 36, 15, 5);
      ctx.fillRect(x + 4, y - 33, 6, 2);
      ctx.fillStyle = '#111827';
      ctx.fillRect(x + 3, y - 29, 2, 3);
      ctx.fillRect(x - 1, y - 24, 8, 2);

      text(ctx, `${score()}`, W - 12, 14, { align: 'right', size: 13, color: '#1e3a8a', weight: 800 });
      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.ellipse(17, 14, 5, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      text(ctx, `× ${g.coinsTaken}`, 27, 14, { align: 'left', size: 13, color: '#1e3a8a', weight: 700 });
      if (g.state === 'ready') overlay(ctx, 'Mario Run', 'Jump: Space / ↑ or click · hold to jump higher · stomp the walkers');
      if (g.state === 'over') overlay(ctx, `Game over · ${score()}`, 'Click or press Space to play again');
    },
  };
}

export const GAMES = [
  { id: 'snake', label: 'Snake', create: createSnake },
  { id: 'tank', label: 'Tank', create: createTank },
  { id: 'runner', label: 'Mario Run', create: createRunner },
];

// ─── The panel ───────────────────────────────────────────────────────────────

const LoaderGames = () => {
  const t = useT();
  const [gameId, setGameId] = useState(() => (GAMES.some((x) => x.id === readGame()) ? readGame() : 'snake'));
  const [best, setBest] = useState(readBest);
  const [score, setScore] = useState(0);
  const [focused, setFocused] = useState(false);
  const canvasRef = useRef(null);
  const gameRef = useRef(null);

  const choose = (id) => {
    setGameId(id);
    writeGame(id);
    canvasRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return undefined; // no canvas support (tests) — nothing to draw
    if (!ctx.roundRect) ctx.roundRect = function roundRect(x, y, w, h) { this.rect(x, y, w, h); };
    const game = GAMES.find((x) => x.id === gameId).create();
    gameRef.current = game;
    setScore(0);

    // Crisp on high-density screens.
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let raf;
    let last = performance.now();
    let shown = -1;
    let wasOver = false;
    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      game.update(dt);
      game.draw(ctx, now / 1000);
      if (game.score !== shown) { shown = game.score; setScore(shown); }
      if (game.over && !wasOver) {
        setBest((prev) => {
          if ((prev[gameId] || 0) >= game.score) return prev;
          const next = { ...prev, [gameId]: game.score };
          writeBest(next);
          return next;
        });
      }
      wasOver = game.over;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [gameId]);

  const toGame = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [((e.clientX - rect.left) / rect.width) * W, ((e.clientY - rect.top) / rect.height) * H];
  };
  const swipeRef = useRef(null);
  const onPointerDown = (e) => {
    canvasRef.current?.focus({ preventScroll: true });
    const [x, y] = toGame(e);
    swipeRef.current = { x: e.clientX, y: e.clientY };
    gameRef.current?.pointer('down', x, y);
  };
  const onPointerMove = (e) => { const [x, y] = toGame(e); gameRef.current?.pointer('move', x, y); };
  const onPointerUp = (e) => {
    const [x, y] = toGame(e);
    gameRef.current?.pointer('up', x, y);
    // A swipe on a touch screen steers the snake.
    const start = swipeRef.current;
    swipeRef.current = null;
    if (start && e.pointerType !== 'mouse' && gameRef.current?.swipe) {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.hypot(dx, dy) > 30) gameRef.current.swipe(dx, dy);
    }
  };
  const onKey = (down) => (e) => {
    if (gameRef.current?.key(e, down)) e.preventDefault(); // arrows/Space don't scroll the page
  };

  return (
    <div className="w-full max-w-[480px] rounded-xl overflow-hidden border border-gray-200 bg-slate-900 select-none">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 text-xs">
        <span className="text-slate-300 font-medium px-1 hidden sm:inline">{t('mediaAI.games.title')}</span>
        <div role="tablist" className="flex gap-1">
          {GAMES.map((game) => (
            <button
              key={game.id}
              type="button"
              role="tab"
              aria-selected={gameId === game.id}
              onClick={() => choose(game.id)}
              className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                gameId === game.id ? 'bg-white text-slate-900' : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              {game.label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-slate-400 tabular-nums whitespace-nowrap">
          {score} · {t('mediaAI.games.best')} {best[gameId] || 0}
        </span>
      </div>
      <canvas
        ref={canvasRef}
        tabIndex={0}
        aria-label={t('mediaAI.games.aria', { game: GAMES.find((x) => x.id === gameId).label })}
        onKeyDown={onKey(true)}
        onKeyUp={onKey(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => gameRef.current?.pointer('leave', 0, 0)}
        onContextMenu={(e) => e.preventDefault()}
        className="block w-full aspect-video outline-none cursor-crosshair touch-none"
        style={{ imageRendering: 'auto' }}
      />
      <p className={`px-3 py-1 text-[11px] ${focused ? 'text-emerald-300' : 'text-slate-400'}`}>
        {focused ? t('mediaAI.games.playing') : t('mediaAI.games.clickToPlay')}
      </p>
    </div>
  );
};

export default LoaderGames;
